import { FC, useState, useEffect } from "react";
import { SubClassMstDTO } from "Types/Admin/AdminSubClassType";


interface SubClassTableProps {
  subClasses: SubClassMstDTO[];
  onSelectSubClass: (ids: number[]) => void;
  onDeleteSubClass: (id: string) => void;
  onViewSubClass: (id: string) => void;
  onEditSubClass: (id: string) => void;
  selectedSubClass: string | null;
  isLoading?: boolean;
  isError?: boolean;
  page: number;
  pageSize: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export const SubClassTable: FC<SubClassTableProps> = ({
  subClasses,
  onSelectSubClass,
  onDeleteSubClass,
  onViewSubClass,
  onEditSubClass,
  selectedSubClass,
  isLoading = false,
  isError = false,
  page,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}) => {
  const [selectAll, setSelectAll] = useState(false);
  const [selectedSubClasses, setSelectedSubClasses] = useState<number[]>([]);

  useEffect(() => {
  if (subClasses.length === 0) {
    setSelectedSubClasses([]);
    setSelectAll(false);
  }
}, [subClasses]);

  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const startItem = totalItems === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, totalItems);

  const handleSelectAll = () => {
    const newSelectAll = !selectAll;
    setSelectAll(newSelectAll);

    if (newSelectAll) {
      const currentPageSubClassIds = subClasses.map((subClass) => subClass.id);
      setSelectedSubClasses(currentPageSubClassIds);

      if (currentPageSubClassIds.length > 0) {
        onSelectSubClass(currentPageSubClassIds);
      }
    } else {
      setSelectedSubClasses([]);
      onSelectSubClass([]);
    }
  };

  const handleSelectSubClass = (id: number, checked: boolean) => {
    let newSelectedSubClasses: number[];

    if (checked) {
      newSelectedSubClasses = [...selectedSubClasses, id];
    } else {
      newSelectedSubClasses = selectedSubClasses.filter(
        (subClassId) => subClassId !== id,
      );
    }

    setSelectedSubClasses(newSelectedSubClasses);

    if (newSelectedSubClasses.length > 0) {
      onSelectSubClass(newSelectedSubClasses);
    } else {
      onSelectSubClass([]);
    }

    const currentPageSubClassIds = subClasses.map((subClass) => subClass.id);

    setSelectAll(
      currentPageSubClassIds.every((id) => newSelectedSubClasses.includes(id)),
    );
  };

  return (
    <div className="font-body tracking-wider overflow-x-auto">
      {isError ? (
        <div className="py-10 text-center text-red-500">
          Error loading sub categories
        </div>
      ) : (
        <>
        <div className="relative">
          {isLoading && (
            <div className="absolute inset-0 bg-white/60 z-10 flex items-center justify-center min-h-[200px]">
              <div className="flex flex-col items-center gap-2">
                <div className="w-8 h-8 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
                <span className="text-sm font-medium text-gray-600">Loading...</span>
              </div>
            </div>
          )}
          <table className="min-w-full">
            <thead>
              <tr className="border-b-2 border-gray-300">
                <th className="py-3 px-4 text-left left-0 bg-white">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                  Sub Category Code
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                  Sub Category Name
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                  Category Name
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500 whitespace-nowrap">
                  Status
                </th>
                <th className="py-3 px-4 text-left text-sm font-semibold text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {subClasses.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-6 text-center text-gray-500">
                    No Sub Category Found
                  </td>
                </tr>
              ) : (
                subClasses.map((subClass) => (
                    <tr
                      key={subClass.subClassCode}
                      className={`border-t ${
                        selectedSubClass === subClass.subClassCode
                          ? "bg-blue-50"
                          : ""
                      }`}
                      onClick={() => {
                        const newSelection = selectedSubClasses.includes(
                          subClass.id,
                        )
                          ? selectedSubClasses.filter(
                              (id) => id !== subClass.id,
                            )
                          : [...selectedSubClasses, subClass.id];
                        onSelectSubClass(newSelection);
                      }}
                    >
                      <td className="py-3 px-4">
                        <input
                          type="checkbox"
                          className="w-4 h-4"
                          checked={selectedSubClasses.includes(subClass.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectSubClass(subClass.id, e.target.checked);
                          }}
                        />
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {subClass.subClassCode}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {subClass.subClassName}
                      </td>
                      <td className="py-3 px-4 text-sm">
                         {subClass.categoryName || subClass.categoryCode}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            subClass.isActive === "Y"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {subClass.isActive === "Y" ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-3 px-4 flex gap-2">
                        <button
                          className="text-blue-500 hover:text-blue-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewSubClass(subClass.subClassCode);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-eye-icon lucide-eye"
                          >
                            <path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        </button>

                        <button
                          className="text-green-500 hover:text-green-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditSubClass(subClass.subClassCode);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-pencil-icon lucide-pencil"
                          >
                            <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z" />
                            <path d="m15 5 4 4" />
                          </svg>
                        </button>

                        <button
                          className="text-red-500 hover:text-red-700"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteSubClass(subClass.subClassCode);
                          }}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="lucide lucide-trash2-icon lucide-trash-2"
                          >
                            <path d="M3 6h18" />
                            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
                            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
                            <line x1="10" x2="10" y1="11" y2="17" />
                            <line x1="14" x2="14" y1="11" y2="17" />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>

          <div className="flex justify-between items-center mt-4 text-sm">
            <div className="hidden md:flex">
              Showing {startItem} to {endItem} of {totalItems} entries
            </div>
            <div className="flex items-center gap-4">
              <select
                className="p-2 border rounded"
                value={pageSize}
                onChange={(e) => {
                  onPageSizeChange(Number(e.target.value));
                }}
              >
                {[100, 500, 1000, 1500].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>

              <div className="flex items-center gap-2">
                <button
                  className="px-2 py-1 border rounded disabled:opacity-50"
                  disabled={page === 1 || isLoading}
                  onClick={() => onPageChange(1)}
                >
                  &lt;&lt;
                </button>
                <button
                  className="px-2 py-1 border rounded disabled:opacity-50"
                  disabled={page === 1 || isLoading}
                  onClick={() => onPageChange(page - 1)}
                >
                  &lt;
                </button>
                <span className="px-2 py-1 bg-gray-100 border rounded">
                  {page}
                </span>
                <button
                  className="px-2 py-1 border rounded disabled:opacity-50"
                  disabled={page === totalPages || totalPages === 0 || isLoading}
                  onClick={() => onPageChange(page + 1)}
                >
                  &gt;
                </button>
                <button
                  className="px-2 py-1 border rounded disabled:opacity-50"
                  disabled={page === totalPages || totalPages === 0 || isLoading}
                  onClick={() => onPageChange(totalPages)}
                >
                  &gt;&gt;
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
